import React, { useEffect, useState, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client/react';
import { ROUTES } from 'config/Nav';
import { HOME_POSTS } from 'lib/graphql/queries/home';
import { Hero } from 'molecules';
import { PageProps } from 'types/page';
import Home from './Home';
import useStyles from './styles';
import { reportError } from 'lib/errors';
import { LoadingError } from 'atoms';
import { HomePosts } from 'types/home';
import {
  hasDisplayableHomePosts,
  isHomeFeedLoading,
  shouldRenderHero,
} from './Home.utils';

/**
 * Query variables for home posts pagination
 */
const QUERY_VARIABLES = {
  limit: 9,
  skip: 0,
} as const;

/**
 * Props for the Home page index component
 */
interface HomePageProps extends PageProps {
  /**
   * Initial home posts data from SSR (getStaticProps)
   */
  posts: HomePosts;
}

/**
 * Home Page Index Component
 *
 * Wrapper component for the Home page that handles data fetching and state management.
 * Initializes with SSR data and manages client-side updates to prevent flicker.
 *
 * Features:
 * - Initializes with SSR data from getStaticProps
 * - Manages client-side data updates with flicker prevention
 * - Handles hero post (featured post position 0) rendering
 * - Passes data to Home presentation component
 *
 * @param props - Component props including SSR posts data
 * @returns Home page with `<main>` landmark: hero and layout-wrapped feed
 */
const Index = ({ posts, ...props }: HomePageProps): React.JSX.Element => {
  const { classes } = useStyles();
  // Initialize with SSR data immediately
  const [homePosts, setHomePosts] = useState<HomePosts | null>(posts);
  // Track if we've set initial data to prevent unnecessary updates from cache
  const hasInitialData = useRef(!!posts);
  // Track previous posts length to detect when fetchMore adds new posts
  const prevPostsLengthRef = useRef(posts?.publishedPosts?.length || 0);

  // Memoize variables to prevent unnecessary re-renders
  const variables = useMemo(() => QUERY_VARIABLES, []);

  const { data, loading, networkStatus, error, fetchMore } = useQuery<HomePosts>(
    HOME_POSTS,
    {
      fetchPolicy: 'cache-first', // Use cache-first to avoid unnecessary re-fetch
      nextFetchPolicy: 'cache-first', // Keep using cache for subsequent fetches
      variables,
      // Enable network status notifications so loadingMore works correctly for fetchMore
      notifyOnNetworkStatusChange: true,
    },
  );

  // Prefer a query payload that actually has posts. The static fallback is an empty
  // object with settings only, and that must not count as loaded homepage content.
  const displayPosts = hasDisplayableHomePosts(data) ? data : homePosts;
  const isLoading = isHomeFeedLoading({
    posts: displayPosts,
    loading: (loading && networkStatus !== 3) || networkStatus === 4,
    hasQueryResult: Boolean(data),
    error,
  });
  const loadingMore = networkStatus === 3;

  useEffect(() => {
    // Only update state if we have new data AND either:
    // 1. We don't have initial SSR data yet, OR
    // 2. We're actively loading (not just cache resolution), OR
    // 3. Data has actually changed (fetchMore added new posts)
    if (data) {
      const newPostsLength = data.publishedPosts?.length || 0;
      // If we already have initial data and this is just cache resolution (not a real fetch),
      // skip the update to prevent unnecessary re-render that causes image flicker
      // But allow updates when fetchMore adds new posts (length increased)
      if (hasInitialData.current && !loading && networkStatus === 7) {
        // networkStatus 7 = ready (cache resolved, no network request)
        // Check if data actually changed by comparing publishedPosts length.
        // An empty static fallback must still accept the first real query result.
        if (
          prevPostsLengthRef.current === newPostsLength &&
          hasDisplayableHomePosts(homePosts)
        ) {
          // Skip update to prevent flicker when cache resolves with same data
          return;
        }
      }
      // Update previous length and state
      prevPostsLengthRef.current = newPostsLength;
      setHomePosts(data);
      hasInitialData.current = true;
    }
  }, [data, homePosts, loading, networkStatus]);

  /**
   * Renders the hero post (featured post position 0) or error state
   *
   * @returns A `<section>` with the hero or error state, or null if no slot-0 post
   */
  const getHero = (): React.JSX.Element | null => {
    if (error) {
      return (
        <section aria-label="Featured post">
          <LoadingError
            error={error}
            failedTarget={'featured post banner'}
            reportError={reportError}
          />
        </section>
      );
    }

    // Static HTML can be days old. Wait for this page's query before painting the hero.
    if (!shouldRenderHero(data?.homepageSettings)) return null;

    if (isLoading && !data?.featuredPosts?._0) {
      return (
        <section aria-label="Featured post">
          <Hero
            postTitle=""
            postUrl={ROUTES.HOME.to}
            alt=""
            src=""
            isLoading
            sizes="100vw"
          />
        </section>
      );
    }

    if (data?.featuredPosts?._0) {
      const { title, slug, image } = data.featuredPosts._0;
      return (
        <section aria-label="Featured post">
          <Hero
            postTitle={title}
            postUrl={ROUTES.HOME.to + slug}
            alt={title}
            src={image ? image.sourceUrl : ''}
            isLoading={isLoading}
            sizes="100vw"
          />
        </section>
      );
    }

    // Handle the case where there is no Hero Post set (Feature Post slot 0)
    return null;
  };

  const hero = getHero();

  return (
    <main
      className={clsx(classes.main, !hero && classes.mainWithoutHero)}
      data-layout="home"
    >
      {hero}
      <Home
        {...props}
        homePosts={displayPosts}
        error={error}
        fetchMore={fetchMore}
        variables={variables}
        loadingMore={loadingMore}
        loading={isLoading}
      />
    </main>
  );
};

export default Index;
